import * as THREE from 'three'
import getChromaKeyMaterial from './chromakey/ChromaKeyMaterial'
import { loadImageAsync } from '../utils/AssetLoader'

export default class VideoPlane {
	constructor(
		width,
		height,
		url,
		onVideoEnd,
		imageUrl = null,
		chromaColor = null,
		roomId = null,
		addToRenderLoop = null,
		removeFromRenderLoop = null,
		videoReadyState,
	) {
		if (typeof width !== 'number' || typeof height !== 'number') {
			throw new Error('width and height must be numbers!')
		}
		this.addToScene = this.addToScene.bind(this)
		this.removeFromScene = this.removeFromScene.bind(this)
		this.setTransform = this.setTransform.bind(this)
		this.setPosition = this.setPosition.bind(this)
		this.setRotation = this.setRotation.bind(this)
		this.play = this.play.bind(this)
		this.pause = this.pause.bind(this)
		this.end = this.end.bind(this)

		this.videoLoadedState = this.videoLoadedState.bind(this)
		this.isPlaying = this.isPlaying.bind(this)
		this.isVisible = this.isVisible.bind(this)

		this.imageUrl = imageUrl
		this.image = null
		if (imageUrl) {
			const imageGeometry = new THREE.PlaneGeometry(1, 1)
			const imageMaterial = new THREE.MeshBasicMaterial()
			this.image = new THREE.Mesh(imageGeometry, imageMaterial)
			this.image.visible = false
			loadImageAsync(
				imageUrl,
				(image) => {
					this.image.material.map = image
					this.image.material.needsUpdate = true
					this.image.material.transparent = true
					this.image.visible = true
				},
				null,
				(error) => {
					console.log(
						'error: ' + error + ', loading image with url: ' + url,
					)
				},
				roomId,
			)
		}

		this.videoReadyState = videoReadyState
		this.roomId = roomId
		this.onVideoEnd = onVideoEnd
		this.video = document.createElement('video')
		this.video.src = url
		this.video.crossOrigin = 'anonymous'
		this.video.setAttribute('webkit-playsinline', '')
		this.video.setAttribute('playsinline', '')
		this.video.currentTime = 1
		this.video.playsInline = true
		this.video.muted = true

		this.video.load()
		if (this.videoReadyState) {
			this.video.currentTime = 0
		} else {
			this.video.currentTime = 1
		}
		this.video.addEventListener('ended', this.end, false)
		if (this.videoReadyState) {
			this.video.addEventListener(
				'loadeddata',
				this.videoLoadedState,
				false,
			)
		}

		const texture = new THREE.VideoTexture(this.video)
		texture.minFilter = THREE.LinearFilter
		texture.magFilter = THREE.LinearFilter
		texture.format = THREE.RGBFormat

		const geometry = new THREE.PlaneGeometry(width, height)
		const material =
			chromaColor === null
				? new THREE.MeshBasicMaterial({ side: THREE.FrontSide })
				: getChromaKeyMaterial(texture, chromaColor)
		material.map = texture
		material.needsUpdate = true
		this.plane = new THREE.Mesh(geometry, material)
		this.plane.visible = false
		this.plane.userData.isVideoPlane = true
		this.renderUUID = null
		this.addToRenderLoop = addToRenderLoop
		this.removeFromRenderLoop = removeFromRenderLoop
	}

	videoLoadedState() {
		if (this.video && this.video.readyState === 4 && this.videoReadyState) {
			this.plane.visible = true
			this.plane.userData.shouldBeVisible = true
			this.videoReadyState(true)
		}
	}

	addToScene(scene) {
		scene.add(this.plane)
		this.renderUUID = this.addToRenderLoop()
		if (this.imageUrl) {
			scene.add(this.image)
		}
	}

	removeFromScene(scene) {
		scene.remove(this.plane)
		if (this.renderUUID) {
			this.removeFromRenderLoop(this.renderUUID)
			this.renderUUID = null
		}
		if (this.imageUrl) {
			scene.remove(this.image)
			this.image = null
		}
		this.plane = null
		this.pause()
		this.video = null
	}

	setTransform(transform, imageTransform = null) {
		this.plane.matrix = transform
		this.plane.matrix.decompose(
			this.plane.position,
			this.plane.quaternion,
			this.plane.scale,
		)

		if (this.imageUrl) {
			this.image.matrix = imageTransform
			this.image.matrix.decompose(
				this.image.position,
				this.image.quaternion,
				this.image.scale,
			)
		}

		// view-1
		if (this.roomId === '61eedd6535a1c209ffa18b23') {
			if (this.video.src.includes('76603salesforce_3-cropped.mp4')) {
				this.setRotation(0, 1.592, 0)
				this.setPosition(-5.56, -1.35, 2.32)
				this.setScale(0.022, 0.025, 1)
				this.playActions()
			}
			if (this.video.src.includes('94489EinsteinNFTanim_v004.mp4')) {
				this.setRotation(0, 1.592, 0)
				this.setPosition(-9.28, -0.200, -1.04)
				this.setScale(0.008, 0.0095, 1)
				this.playActions()
			}
			if (this.video.src.includes('64439CodeyNFTanim_v004.mp4')) {
				this.setRotation(0, 1.592, 0)
				this.setPosition(-9.28, -0.200, 1.05)
				this.setScale(0.008, 0.0095, 1)
				this.playActions()
			}
			if (this.video.src.includes('15915AstroNFTanim_v004.mp4')) {
				this.setRotation(0, 1.592, 0)
				this.setPosition(-9.28, -0.200, 0)
				this.setScale(0.008, 0.0098, 1)
				this.playActions()
			}
			if (this.video.src.includes('34746salesforce.mp4')) {
				this.setRotation(-3.142, 2.93, -3.142)
				this.setPosition(-9.42, -0.31, -4.2)
				this.setScale(0.007, 0.009, 1)
				this.playActions()
			}
		}
		// view-2
		if (this.roomId === '61eedd78073e5f4454b7d77e') {
			if (this.video.src.includes('79770EinsteinNFTanim_v004.mp4')) {
				this.setRotation(-3.142, 0, -3.142)
				this.setPosition(0.12, -0.2, 6.77)
				this.setScale(0.008, 0.0098, 1)
				this.playActions()
			}
			if (this.video.src.includes('17038CodeyNFTanim_v004.mp4')) {
				this.setRotation(-3.142, 0, -3.142)
				this.setPosition(2.23, -0.2, 6.77)
				this.setScale(0.008, 0.0098, 1)
				this.playActions()
			}
			if (this.video.src.includes('31513AstroNFTanim_v004.mp4')) {
				this.setRotation(-3.142, 0, -3.142)
				this.setPosition(1.17, -0.2, 6.71)
				this.setScale(0.008, 0.0098, 1)
				this.playActions()
			}
			if (this.video.src.includes('10419salesforce.mp4')) {
				this.setRotation(-3.142, 1.4, -3.142)
				this.setPosition(-3.34, -0.42, 7.69)
				this.setScale(0.015, 0.018, 1)
				this.playActions()
			}
			if (this.video.src.includes('NFT_banner_v006.mp4')) {
				this.setPosition(1.5, 0.56, 8.04);
				this.setRotation(0, -3.2, 0);
				this.setScale(0.04, 0.0033, 0.5);
				this.playActions();
			}
			if (this.video.src.includes('44636salesforce_3-cropped')) {
				this.setRotation(0, 3 * (Math.PI / 2), 0)
				this.setPosition(5.5, -2, 1.6)
				this.setScale(0.022, 0.025, 1)
				this.playActions()
			}
		}
		// view-3
		if (this.roomId === '61f15060407fd4835c71e99f') {
			if (this.video.src.includes('79399EinsteinNFTanim_v004.mp4')) {
				this.setRotation(-0.001, -0.012, -0.001)
				this.setPosition(4.16, -0.4, -8.79)
				this.setScale(0.015, 0.018, 1)
				this.playActions()
			}
			if (this.video.src.includes('22373CodeyNFTanim_v004.mp4')) {
				this.setRotation(3.141, -3.3, 3.141)
				this.setPosition(0.22, -0.4, -8.79)
				this.setScale(0.015, 0.018, 1)
				this.playActions()
			}
			if (this.video.src.includes('86930AstroNFTanim_v004.mp4')) {
				this.setRotation(-0.001, 0.038, -0.001)
				this.setPosition(2.2, -0.4, -8.79)
				this.setScale(0.015, 0.018, 1)
				this.playActions()
			}
			if (this.video.src.includes('92613salesforce.mp4')) {
				this.setRotation(0, -1.412, 0)
				this.setPosition(7.04, -0.39, -3.23)
				this.setScale(0.015, 0.016, 1)
				this.playActions()
			}
			if (this.video.src.includes('13685salesforce_3-cropped.mp4')) {
				this.setPosition(-0.01, -2.37, 4.06)
				this.setRotation(-3.141, 0.0, -3.141)
				this.setScale(0.012, 0.02, 1)
				this.playActions()
			}
			if (this.video.src.includes('NFT_banner_v006.mp4')) {
				this.setPosition(2, 0.76, -8.04);
				this.setRotation(0, 0, 0);
				this.setScale(0.056, 0.0035, 0.5);
				this.playActions();
			}
		}
		// view-4
		if (this.roomId === '61f150a8bae7d05f47707911') {
			if (this.video.src.includes('33652EinsteinNFTanim_v004.mp4')) {
				this.setRotation(0, 1.55, 0)
				this.setPosition(-9.28, -0.93, -4.7)
				this.setScale(0.036, 0.043, 1)
				this.playActions()
			}
			if (this.video.src.includes('44907CodeyNFTanim_v004.mp4')) {
				this.setRotation(0, 1.55, 0)
				this.setPosition(-9.28, -0.93, 4.71)
				this.setScale(0.036, 0.043, 1)
				this.playActions()
			}
			if (this.video.src.includes('95487AstroNFTanim_v004.mp4')) {
				this.setRotation(0, 1.55, 0)
				this.setPosition(-9.28, -0.93, 0)
				this.setScale(0.036, 0.043, 1)
				this.playActions()
			}
			if (this.video.src.includes('52719salesforce_3-cropped.mp4')) {
				this.setRotation(-3.141, -1.51, -3.141)
				this.setPosition(9.0, -2.05, 3.17)
				this.setScale(0.022, 0.03, 1)
				this.playActions()
			}
			if (this.video.src.includes('66381salesforce.mp4')) {
				this.setRotation(0.001, 0, Math.PI /1)
				this.setPosition(2.2, -0.620, -8.45)
				this.setScale(-0.0265, -0.027, 1)
				this.playActions()
			}
			if (this.video.src.includes('NFT_banner_v006.mp4')) {
				this.setPosition(-9.8, 2.155, 0.04);
				this.setRotation(0, 1.57, 0);
				this.setScale(0.153, 0.01, 0.5);
				this.playActions();
			}
			if (this.video.src.includes('52719salesforce_3-cropped')) {
				this.setPosition(9.0, -2.05, 3.17);
				this.setRotation(-3.141, -1.51, -3.141);
				this.setScale(0.022, 0.03, 1);
				this.playActions();
			}

		}

		//view-5
		if(this.roomId === '621edb23f4e810f086e0a264'){
			console.log("SRC", this.video.src)
			if(this.video.src.includes('NFT_banner_v006.mp4')){
				this.setPosition(2.700, 1.015, 9.790);
				this.setRotation(0, 3.140, 0);
				this.setScale(0.070, 0.005, 0.5);
				this.playActions();
			}
			if (this.video.src.includes('CodeyNFTanim_v004.mp4')) {
				this.setPosition(5.080, -0.470, 9.980);
				this.setRotation(-3.141, 0, -3.141);
				this.setScale(0.018, 0.0217, 1);
				this.playActions();
			}
			if (this.video.src.includes('74027EinsteinNFTanim_v004.mp4')) {
				this.setPosition(0.380, -0.470, 9.980);
				this.setRotation(-3.141, 0, -3.141);
				this.setScale(0.018, 0.0217, 1);
				this.playActions();
			}
			if (this.video.src.includes('AstroNFTanim_v004.mp4')) {
				this.setPosition(2.720, -0.470, 9.980);
				this.setRotation(-3.141, 0, -3.141);
				this.setScale(0.018, 0.0217, 1);
				this.playActions();
			}
			if (this.video.src.includes('77336salesforce_3-cropped.mp4')) {
				this.setPosition(8.060, -2.760, -3.920);
				this.setRotation(-3.141, -1.51, -3.141);
				this.setScale(0.022, 0.03, 1);
				this.playActions();
			}
			if (this.video.src.includes('40119salesforce.mp4')) {
				this.setPosition(-7.820, -1.015, 7.800);
				this.setRotation(0, 1.57, 0);
				this.setScale(0.045, 0.043, 0.5);
				this.playActions();
			}
		}
	}
	
	playActions() {
		this.video.play()
		this.plane.visible = true
		this.plane.userData.shouldBeVisible = true
		if (this.imageUrl) {
			this.image.visible = false
		}
	}

	setPosition(x, y, z) {
		this.plane.position.set(x, y, z)
	}

	setRotation(x, y, z) {
		this.plane.rotation.x = x
		this.plane.rotation.y = y
		this.plane.rotation.z = z
	}

	setScale(x, y, z) {
		this.plane.scale.x = x
		this.plane.scale.y = y
		this.plane.scale.z = z
	}

	play() {
		/*this.video.play();
        this.plane.visible = true;
        if (this.imageUrl) {
            this.image.visible = false;
        }*/
		if (this?.video?.readyState === 4) {
			this.playActions()
		} else {
			this.video.addEventListener('loadeddata', () => {
				this.playActions()
			})
		}
	}

	pause() {
		this.video.pause()
	}

	end() {
		this.plane.visible = false
		if (this.imageUrl) {
			this.image.visible = true
		}
		if (this.onVideoEnd) {
			this.onVideoEnd()
		}
	}

	isPlaying() {
		return !this.video.paused
	}

	isVisible() {
		return this.plane.visible
	}
}
